import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPatch } from '../api/client.js';
import { ChipInput } from './ChipInput.jsx';
import { BrandColorField } from './BrandColorField.jsx';
import { darken } from '../utils/color.js';
import { GOAL_OPTIONS, GOAL_LABEL, DEFAULT_BRAND_COLOR } from '../constants/brand.js';

/**
 * BrandBrief (ADR-0027) — the brand owner's direct steering surface.
 *
 * This is the clear, prominent home for "your input": vision, goal, current
 * focus, voice, and always/never rules. Everything the AI generates downstream
 * (plan, pillars, posts) honors these fields at highest priority. Read view by
 * default; Edit reveals the form and PATCHes /projects/:key.
 */

export function BrandBrief({ projectKey, project }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const originalForm = useRef(null);

  const startEdit = () => {
    const snapshot = {
      mission: project.mission || '',
      primary_goal: project.primary_goal || '',
      current_focus: project.current_focus || '',
      current_offer: project.current_offer || '',
      positioning: project.positioning || '',
      audience: project.audience || '',
      voice: project.voice || '',
      custom_instructions: project.custom_instructions || '',
      topics_emphasize: project.topics_emphasize || [],
      topics_avoid: project.topics_avoid || [],
      brand_color: project.brand_colors?.primary || DEFAULT_BRAND_COLOR,
    };
    setForm(snapshot);
    originalForm.current = snapshot;
    setConfirmDiscard(false);
    setEditing(true);
  };

  const saveM = useMutation({
    mutationFn: () => apiPatch(`/projects/${encodeURIComponent(projectKey)}`, {
      mission: form.mission.trim() || null,
      primary_goal: form.primary_goal || null,
      current_focus: form.current_focus.trim() || null,
      current_offer: form.current_offer.trim() || null,
      positioning: form.positioning.trim(),
      audience: form.audience.trim(),
      voice: form.voice.trim(),
      custom_instructions: form.custom_instructions.trim() || null,
      topics_emphasize: form.topics_emphasize,
      topics_avoid: form.topics_avoid,
      brand_colors: { primary: form.brand_color, primary_dark: darken(form.brand_color) },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectKey] });
      // Loop Status Stage 1 reads positioning/audience/voice — refresh it too,
      // otherwise it shows the pre-save state until its 15s staleTime expires.
      qc.invalidateQueries({ queryKey: ['loop-status', projectKey] });
      setEditing(false);
      setConfirmDiscard(false);
      setMsg({ kind: 'ok', text: 'Brief saved.' });
      setTimeout(() => setMsg(null), 5000);
    },
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Compare against the snapshot captured when the editor opened; arrays and
  // recipient objects are covered by the structural (JSON) compare.
  const isDirty = () => !!originalForm.current && JSON.stringify(form) !== JSON.stringify(originalForm.current);
  const requestCancel = () => { if (isDirty()) setConfirmDiscard(true); else setEditing(false); };
  const discardEdits = () => { setConfirmDiscard(false); setEditing(false); };

  // Any onboarded brand always has positioning/audience/voice, so the toggle
  // should read "Edit" (not "Add your input") once those exist — only a truly
  // blank brief gets the "Add your input" affordance.
  const hasAny = project.mission || project.primary_goal || project.current_focus
    || project.custom_instructions || project.topics_emphasize?.length || project.topics_avoid?.length
    || project.positioning || project.audience || project.voice;

  return (
    <section className={`post-section brand-brief overview-card${editing ? ' is-editing' : ''}`}>
      <div className="section-h-row">
        <h2>Brand brief — your direction</h2>
        {!editing && (
          <button type="button" className="btn-outline" onClick={startEdit}>
            {hasAny ? 'Edit' : 'Add your input'}
          </button>
        )}
      </div>
      <p className="muted small brand-brief-lead">
        Everything Markaii generates follows this brief first. Tell it your vision, your goal, and what to push right now — the AI fills the rest and you approve every post.
      </p>

      {msg && <div className={`post-action-msg ${msg.kind}`}>{msg.text}</div>}

      {!editing && (
        <div className="brief-readout">
          <BriefField label="Your vision" value={project.mission} empty="What's the bigger purpose of this brand's marketing?" />
          <BriefField label="Primary goal" value={project.primary_goal ? (GOAL_LABEL[project.primary_goal] ?? project.primary_goal) : null} empty="Pick what matters most right now." />
          <BriefField label="Push right now" value={project.current_focus} empty="A launch, a season, a feature — what to lean into this month." />
          <BriefField label="Current offer" value={project.current_offer} empty="The live offer the AI may quote to buying-intent leads — exact terms only (trial length, card required or not)." />
          <BriefField label="Always / never rules" value={project.custom_instructions} empty="Hard rules for every post (e.g. “never use exclamation marks”, “always mention the 14-day trial”)." mono />
          <BriefChips label="Lean into" items={project.topics_emphasize} />
          <BriefChips label="Stay away from" items={project.topics_avoid} />
          <div className="brief-divider" />
          <BriefField label="Positioning" value={project.positioning} mono />
          <BriefField label="Audience" value={project.audience} mono />
          <BriefField label="Voice" value={project.voice} mono />
          <BriefColor label="Brand color" value={project.brand_colors?.primary} />
        </div>
      )}

      {editing && form && (
        <form
          className="brief-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!form.positioning.trim() || !form.audience.trim() || !form.voice.trim()) {
              setMsg({ kind: 'err', text: 'Positioning, audience, and voice can’t be empty.' });
              return;
            }
            saveM.mutate();
          }}
        >
          <fieldset className="brief-fieldset" disabled={saveM.isPending}>
          <label className="login-field">
            <span>Your vision <small className="muted">— the why, in your words</small></span>
            <textarea rows={2} value={form.mission} onChange={set('mission')}
              placeholder="e.g. Position CliLancers as the freelancer's anti-late-payment ally — practical, never preachy." />
          </label>

          <label className="login-field">
            <span>Primary goal</span>
            <select value={form.primary_goal} onChange={set('primary_goal')}>
              {GOAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>

          <label className="login-field">
            <span>Push right now <small className="muted">— time-bound focus</small></span>
            <textarea rows={2} value={form.current_focus} onChange={set('current_focus')}
              placeholder="e.g. This month, push the new automated-reminders feature ahead of everything else." />
          </label>

          <label className="login-field">
            <span>Current offer <small className="muted">— quoted to buying-intent leads; exact terms only</small></span>
            <textarea rows={2} value={form.current_offer} onChange={set('current_offer')}
              placeholder="Describe the live offer exactly as it's true — trial length, whether a card is required, what's included. Leave empty and the AI makes no offer claims." />
          </label>

          <label className="login-field">
            <span>Always / never rules <small className="muted">— applied to every post</small></span>
            <textarea rows={3} value={form.custom_instructions} onChange={set('custom_instructions')}
              placeholder={'e.g.\nAlways mention the 14-day free trial when relevant.\nNever use more than one emoji.\nKeep captions under 120 words.'} />
          </label>

          <div className="brief-form-2col">
            <div className="login-field">
              <span>Lean into these topics <small className="muted">— Enter to add</small></span>
              <ChipInput value={form.topics_emphasize}
                onChange={(v) => setForm({ ...form, topics_emphasize: v })}
                label="Lean into these topics"
                placeholder="e.g. late-payment psychology" />
            </div>
            <div className="login-field">
              <span>Stay away from <small className="muted">— Enter to add</small></span>
              <ChipInput value={form.topics_avoid}
                onChange={(v) => setForm({ ...form, topics_avoid: v })}
                label="Stay away from these topics"
                placeholder="e.g. generic “hustle” motivation" />
            </div>
          </div>

          <div className="brief-divider" />
          <p className="muted small">Brand basics — the AI extracted these; correct anything that's off.</p>

          <label className="login-field">
            <span>Positioning</span>
            <textarea rows={2} required value={form.positioning} onChange={set('positioning')} />
          </label>
          <label className="login-field">
            <span>Audience</span>
            <textarea rows={4} required value={form.audience} onChange={set('audience')} />
          </label>
          <label className="login-field">
            <span>Voice</span>
            <textarea rows={4} required value={form.voice} onChange={set('voice')} />
          </label>

          <div className="login-field">
            <span>Brand color <small className="muted">— slides &amp; accents</small></span>
            <BrandColorField value={form.brand_color}
              onChange={(v) => setForm({ ...form, brand_color: v })} />
          </div>
          <p className="muted small brief-emails-moved">
            Notification emails now have their own card below the brief — no need to open the full editor to add a recipient.
          </p>
          </fieldset>

          {saveM.isError && (
            <div className="post-action-msg err">
              {saveM.error?.body?.detail ?? saveM.error?.body?.error ?? saveM.error?.message ?? 'Save failed — please try again.'}
            </div>
          )}

          {confirmDiscard && (
            <div className="confirm-banner confirm-banner--danger">
              <p>Discard your unsaved changes to this brief?</p>
              <div className="confirm-banner-actions">
                <button type="button" className="btn-outline" onClick={() => setConfirmDiscard(false)}>Keep editing</button>
                <button type="button" className="btn btn-danger" onClick={discardEdits}>Discard changes</button>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-outline" onClick={requestCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saveM.isPending}>
              {saveM.isPending ? 'Saving…' : 'Save brief'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function BriefField({ label, value, empty, mono }) {
  return (
    <div className="brief-field">
      <span className="brief-label">{label}</span>
      {value
        ? <div className={`brief-value${mono ? ' mono' : ''}`}>{value}</div>
        : <div className="brief-value brief-empty">{empty}</div>}
    </div>
  );
}

function BriefColor({ label, value }) {
  return (
    <div className="brief-field">
      <span className="brief-label">{label}</span>
      {value
        ? (
          <div className="brief-value brief-value--color">
            {/* dynamic user-defined hex — inline background is the intended
                exception to the no-inline-color rule (not a design token). */}
            <span className="brand-swatch" style={{ background: value }} />
            <span className="mono">{value}</span>
          </div>
        )
        : <div className="brief-value brief-empty">No brand color set.</div>}
    </div>
  );
}

function BriefChips({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <div className="brief-field">
      <span className="brief-label">{label}</span>
      <div className="brief-chips">
        {items.map((t, i) => <span key={`${t}-${i}`} className="brief-chip">{t}</span>)}
      </div>
    </div>
  );
}

